import * as yup from 'yup';
import { ArraySchema, ValidationError } from 'yup';

export type YupRule = [RegExp, string];
export type YupFormData<T extends yup.ISchema<any, any>> = yup.InferType<T>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ArrayType = any[] | null | undefined;

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PASSWORD_REGEX = /^(?=.{8,})\S+$/;

yup.addMethod(yup.string, 'email', (message = 'Invalid email format') => {
  return yup.string().test('email', message, (value: any) => {
    if (!value) return true;
    return EMAIL_REGEX.test(value);
  });
});

export const PASSWORD_RULE: YupRule = [
  PASSWORD_REGEX,
  'Password must meet the criteria',
];

function distinctOnProperty<
  TIn extends ArrayType,
  TContext,
  TDefault = undefined,
  TFlags extends yup.Flags = ''
>(
  this: ArraySchema<TIn, TContext, TDefault, TFlags>,
  property: string,
  message: string
) {
  return this.test('distinct-on-property', message, (array, context) => {
    if (!array) {
      return true;
    }

    const errors: ValidationError[] = [];
    const duplicateProperties = array
      .filter(
        (e, i) => array.findIndex((e2) => e2[property] === e[property]) !== i
      )
      .map((e) => e[property]);
    for (let i = 0; i < array.length; i += 1) {
      const element = array[i];
      if (
        element[property] !== '' &&
        duplicateProperties.includes(element[property])
      ) {
        errors.push(
          context.createError({
            path: `${context.path}[${i}].${property}`,
            message,
          })
        );
      }
    }
    if (errors.length > 0) {
      throw new ValidationError(errors);
    }

    return true;
  });
}

yup.addMethod(yup.array, 'distinctOnProperty', distinctOnProperty);

declare module 'yup' {
  export interface ArraySchema<
    TIn extends ArrayType,
    TContext,
    TDefault = undefined,
    TFlags extends yup.Flags = ''
  > {
    distinctOnProperty(
      property: string,
      message: string
    ): ArraySchema<TIn, TContext, TDefault, TFlags>;
  }
}

export default yup;
